import { useState } from "react";
import { ApiError } from "../../lib/api";
import { money } from "../../lib/format";
import type { PayoutMethodSnapshot } from "../../../shared/types";
import { Sheet } from "../../../shared/phone/Sheet";
import { methodFields, methodSummary } from "./payout";
import { CopyRow } from "./parts";

/** What the owner says they paid. A record only: Flashadz never moves the money. */
export interface PaidRecord {
  amountCents: number;
  reference: string | null;
  note: string | null;
  datePaid: string | null;
}

const todayISO = (): string => new Date().toISOString().slice(0, 10);

/**
 * Mark paid on a phone: where to send the money (copied with a tap), then what was paid, with the decimal
 * keypad and the phone's own date picker. `onRecord` writes it; a refusal shows in the sheet.
 */
export function MarkPaidSheet({
  name,
  owedCents,
  method,
  onClose,
  onRecord,
  onCopied,
}: {
  name: string;
  owedCents: number;
  method: PayoutMethodSnapshot | null;
  onClose: () => void;
  onRecord: (record: PaidRecord) => Promise<void>;
  onCopied: (label: string, ok: boolean) => void;
}) {
  const [amount, setAmount] = useState(`$${(owedCents / 100).toFixed(2)}`);
  const [date, setDate] = useState(todayISO());
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const sendTo = methodFields(method)[0];

  const submit = async () => {
    if (busy) return;
    const cents = Math.round(Number.parseFloat(amount.replace(/[^0-9.]/g, "")) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setErr("Enter an amount greater than zero.");
      return;
    }
    if (cents > owedCents) {
      setErr(`That is more than the ${money(owedCents)} owed to ${name}.`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onRecord({ amountCents: cents, reference: reference.trim() || null, note: note.trim() || null, datePaid: date || null });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title="Mark paid"
      className="pa-form-sheet"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      }
      foot={
        <button type="button" className="pn-btn pri wide" onClick={submit} disabled={busy}>
          {busy ? "Recording…" : "Mark as paid"}
        </button>
      }
    >
      <p className="pa-sheet-sub">
        {name} · {money(owedCents)} ready
      </p>
      <div className="pn-grp">
        {sendTo ? (
          <CopyRow label="Send to" value={sendTo[1]} shown={methodSummary(method)} onCopied={onCopied} />
        ) : (
          <div className="pn-row static">
            <span className="m">
              <span className="s">Send to</span>
              <span className="t">{methodSummary(method)}</span>
            </span>
          </div>
        )}
      </div>
      <div className="pn-grp pa-gap">
        <div className="pn-irow pa-f">
          <label htmlFor="pa-mp-amount">Amount</label>
          <input id="pa-mp-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="pn-irow pa-f">
          <label htmlFor="pa-mp-date">Date paid</label>
          <input id="pa-mp-date" type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>
      <div className="pn-grp pa-gap">
        <div className="pn-irow col">
          <label htmlFor="pa-mp-ref">Reference</label>
          <input
            id="pa-mp-ref"
            value={reference}
            placeholder="Paste the transfer or PayPal ID"
            autoComplete="off"
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
        <div className="pn-irow col">
          <label htmlFor="pa-mp-note">Note to partner</label>
          <input
            id="pa-mp-note"
            value={note}
            placeholder="Optional. Shows on their payout receipt"
            autoComplete="off"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
      {err ? (
        <p className="pa-err" role="alert">
          {err}
        </p>
      ) : null}
      <p className="pn-note">You pay {name} directly with these details. Flashadz records it here and on their receipt, and never moves the money.</p>
    </Sheet>
  );
}
